import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  confirmResetPassword,
  confirmSignUp,
  fetchUserAttributes,
  getCurrentUser,
  resetPassword,
  signIn,
  signOut,
  signUp,
  updateUserAttribute
} from "aws-amplify/auth";
import QRCode from "qrcode";
import {
  buildAuthReturnPath,
  DEFAULT_COMPETITION_RETURN,
  getReturnToFromLocation,
  isCompetitionReturnTo,
  normalizeReturnTo
} from "../../lib/authReturn";
import {
  directorModeLabel,
  directorModePath,
  loadCompetitionDirectorSignal,
} from "../../lib/competitionCapabilities";
import { getMyCompetitionPerson, isCompetitionTestAuthEnabled, updateMyCompetitionPerson } from "../../lib/leaderboardApi";
import { CompetitionAuthGate } from "../Competition/CompetitionRoutePanels";
import "./Auth.css";
import "../Leaderboard/Leaderboard.css";
function buildProfileQrPayload({ sub, name, email }) {
  const subValue = String(sub || "").trim();
  if (!subValue) {
    return "";
  }

  const params = new URLSearchParams({ sub: subValue });
  const nameValue = String(name || "").trim();
  const emailValue = String(email || "").trim();

  if (nameValue) {
    params.set("name", nameValue);
  }

  if (emailValue) {
    params.set("email", emailValue);
  }

  return `armorysync://profile?${params.toString()}`;
}

function findMembershipId(memberships, ...systems) {
  const expected = systems.map((item) => String(item || "").toLowerCase());
  const found = (Array.isArray(memberships) ? memberships : []).find((item) =>
    expected.includes(String(item?.system || "").toLowerCase())
  );
  return String(found?.memberId || "").trim();
}

function mergeCompetitionMemberships(existingMemberships, { nraId, uspsaId }) {
  const existing = Array.isArray(existingMemberships) ? existingMemberships : [];
  const retained = existing.filter((item) => {
    const system = String(item?.system || "").toLowerCase();
    return !["nra", "nra-competitor", "uspsa"].includes(system);
  });
  const next = [...retained];
  const nraValue = String(nraId || "").trim();
  const uspsaValue = String(uspsaId || "").trim();
  if (nraValue) {
    next.push({ system: "nra", memberId: nraValue, label: "NRA ID" });
  }
  if (uspsaValue) {
    next.push({ system: "uspsa", memberId: uspsaValue, label: "USPSA Number" });
  }
  return next;
}


function normalizeUserRole(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "instructor" ? "instructor" : "student";
}
function AuthForm({ mode = "sign-in" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isSignUp = mode === "sign-up";
  const requestedReturnTo = useMemo(() => new URLSearchParams(location.search || "").get("returnTo"), [location.search]);
  const hasReturnTo = Boolean(String(requestedReturnTo || "").trim());
  const returnTo = useMemo(() => normalizeReturnTo(requestedReturnTo, DEFAULT_COMPETITION_RETURN), [requestedReturnTo]);
  const competitionAuth = hasReturnTo && isCompetitionReturnTo(returnTo);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [preferredUsername, setPreferredUsername] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [isConfirmStep, setIsConfirmStep] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const title = useMemo(() => {
    if (competitionAuth) {
      return isSignUp ? "Create Range Pocket Account" : "Sign In to Range Pocket";
    }
    return isSignUp ? "Create Account" : "Sign In";
  }, [competitionAuth, isSignUp]);
  const productLabel = competitionAuth ? "Range Pocket Competition" : "ArmorySync";
  const returnPathname = String(returnTo || "").split(/[?#]/)[0];
  const authSubtitle = competitionAuth
    ? returnPathname.startsWith("/matches/manage")
      ? "Open director tools."
      : returnPathname.startsWith("/matches/my")
        ? "View registrations and results."
        : returnPathname.startsWith("/competition/account")
          ? "Manage your profile and match preferences."
          : "Continue to Range Pocket."
    : "Use your ArmorySync account to manage your profile and product access.";
  const signInPath = hasReturnTo ? buildAuthReturnPath("/auth/sign-in", returnTo) : "/auth/sign-in";
  const signUpPath = hasReturnTo ? buildAuthReturnPath("/auth/sign-up", returnTo) : "/auth/sign-up";
  const forgotPasswordPath = hasReturnTo ? buildAuthReturnPath("/auth/forgot-password", returnTo) : "/auth/forgot-password";

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");

    try {
      if (isSignUp) {
        const targetEmail = isConfirmStep ? confirmEmail : email;

        if (isConfirmStep) {
          await confirmSignUp({
            username: targetEmail,
            confirmationCode: confirmCode
          });
          setMessage("Account confirmed. You can sign in now.");
          navigate(signInPath, { replace: true });
        } else {
          const res = await signUp({
            username: email,
            password,
            options: {
              userAttributes: {
                email,
                preferred_username: preferredUsername
              }
            }
          });

          if (res?.nextStep?.signUpStep === "CONFIRM_SIGN_UP") {
            setConfirmEmail(email);
            setIsConfirmStep(true);
            setMessage("Verification code sent to your email.");
          } else {
            setMessage("Sign-up complete. Please sign in.");
            navigate(signInPath, { replace: true });
          }
        }
      } else {
        await signIn({ username: email, password });
        navigate(returnTo, { replace: true });
      }
    } catch (submitError) {
      setError(submitError?.message || "Authentication failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="auth-page">
      <Helmet>
        <title>{title} | {productLabel}</title>
      </Helmet>

      <div className={`auth-card ${competitionAuth ? "auth-card--competition" : ""}`}>
        <div className="auth-context-label">{productLabel}</div>
        <h1>{title}</h1>
        <p className="auth-subtitle">{authSubtitle}</p>

        <form onSubmit={onSubmit} className="auth-form">
          {!isSignUp || !isConfirmStep ? (
            <>
              <label>
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="email"
                  required
                />
              </label>

              {isSignUp && (
                <label>
                  Username
                  <input
                    type="text"
                    value={preferredUsername}
                    onChange={(event) => setPreferredUsername(event.target.value)}
                    autoComplete="username"
                    required
                  />
                </label>
              )}

              <label>
                Password
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  required
                />
              </label>
            </>
          ) : (
            <>
              <label>
                Confirmation Email
                <input
                  type="email"
                  value={confirmEmail}
                  onChange={(event) => setConfirmEmail(event.target.value)}
                  required
                />
              </label>

              <label>
                Verification Code
                <input
                  type="text"
                  value={confirmCode}
                  onChange={(event) => setConfirmCode(event.target.value)}
                  required
                />
              </label>
            </>
          )}

          <button type="submit" disabled={busy}>
            {busy ? "Please wait..." : isSignUp && isConfirmStep ? "Confirm Account" : title}
          </button>
        </form>

        {message && <p className="auth-message">{message}</p>}
        {error && <p className="auth-error">{error}</p>}

        <div className="auth-links">
          {isSignUp ? (
            <Link to={signInPath}>Already have an account? Sign in</Link>
          ) : (
            <>
              <Link to={signUpPath}>Need an account? Sign up</Link>
              <Link to={forgotPasswordPath}>Forgot password?</Link>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const requestedReturnTo = useMemo(() => new URLSearchParams(location.search || "").get("returnTo"), [location.search]);
  const hasReturnTo = Boolean(String(requestedReturnTo || "").trim());
  const returnTo = useMemo(() => getReturnToFromLocation(location, DEFAULT_COMPETITION_RETURN), [location]);
  const competitionAuth = hasReturnTo && isCompetitionReturnTo(returnTo);
  const productLabel = competitionAuth ? "Range Pocket Competition" : "ArmorySync";
  const signInPath = hasReturnTo ? buildAuthReturnPath("/auth/sign-in", returnTo) : "/auth/sign-in";
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [step, setStep] = useState("request");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function handleRequest(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");

    try {
      const result = await resetPassword({ username: email });
      if (result?.nextStep?.resetPasswordStep === "CONFIRM_RESET_PASSWORD_WITH_CODE") {
        setStep("confirm");
        setMessage("Verification code sent. Enter code and new password.");
      } else {
        setMessage("Password reset is complete. You can sign in now.");
      }
    } catch (requestError) {
      setError(requestError?.message || "Unable to start password reset");
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirm(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword
      });
      navigate(signInPath, { replace: true });
    } catch (confirmError) {
      setError(confirmError?.message || "Unable to reset password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="auth-page">
      <Helmet>
        <title>Reset Password | {productLabel}</title>
      </Helmet>

      <div className={`auth-card ${competitionAuth ? "auth-card--competition" : ""}`}>
        <div className="auth-context-label">{productLabel}</div>
        <h1>Reset Password</h1>
        {competitionAuth && <p className="auth-subtitle">Reset your password, then return to Range Pocket.</p>}
        {step === "request" ? (
          <form onSubmit={handleRequest} className="auth-form">
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />
            </label>
            <button type="submit" disabled={busy}>
              {busy ? "Please wait..." : "Send Code"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleConfirm} className="auth-form">
            <label>
              Verification Code
              <input
                type="text"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                required
              />
            </label>
            <label>
              New Password
              <input
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
            <button type="submit" disabled={busy}>
              {busy ? "Please wait..." : "Update Password"}
            </button>
          </form>
        )}

        {message && <p className="auth-message">{message}</p>}
        {error && <p className="auth-error">{error}</p>}

        <div className="auth-links">
          <Link to={signInPath}>Back to sign in</Link>
        </div>
      </div>
    </section>
  );
}

function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [mode, setMode] = useState("view");

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [sub, setSub] = useState("");
  const [newsletter, setNewsletter] = useState(false);
  const [nraId, setNraId] = useState("");
  const [uspsaId, setUspsaId] = useState("");
  const [userRole, setUserRole] = useState("student");
  const [directorSignal, setDirectorSignal] = useState("checking");
  const [person, setPerson] = useState(null);

  const [draftUsername, setDraftUsername] = useState("");
  const [draftNraId, setDraftNraId] = useState("");
  const [draftUspsaId, setDraftUspsaId] = useState("");
  const [draftNewsletter, setDraftNewsletter] = useState(false);

  const [qrDataUrl, setQrDataUrl] = useState("");
  const [profileSignInRequired, setProfileSignInRequired] = useState(false);
  const competitionAccount = location.pathname.startsWith("/competition/");
  const profileTitle = competitionAccount ? "Competition Account | Range Pocket Competition" : "Profile | ArmorySync";
  const profileHeading = competitionAccount ? "Competition Account" : "Profile";
  const profileSubtitle = competitionAccount
    ? "Registration identity and workspace entry"
    : "Manage your ArmorySync identity, preferences, profile QR, and account links.";
  const profileSignInPath = competitionAccount
    ? buildAuthReturnPath("/auth/sign-in", location.pathname)
    : "/auth/sign-in";

  React.useEffect(() => {
    let active = true;

    async function loadProfile() {
      try {
        const user = await getCurrentUser();
        const attrs = await fetchUserAttributes();
        if (!active) {
          return;
        }

        const preferred = attrs?.preferred_username || user?.username || "User";
        const subValue = attrs?.sub || "";
        const newsletterValue = String(attrs?.["custom:subToNewsLetter"] || "false") === "true";
        let personValue = null;
        if (competitionAccount) {
          try {
            personValue = await getMyCompetitionPerson();
          } catch {
            personValue = null;
          }
        }
        const nraValue = findMembershipId(personValue?.memberships, "nra", "nra-competitor") || String(attrs?.["custom:nraId"] || "");
        const uspsaValue = findMembershipId(personValue?.memberships, "uspsa") || String(attrs?.["custom:uspsaId"] || "");
        const roleValue = normalizeUserRole(attrs?.["custom:userRole"] || "student");

        setUsername(preferred);
        setEmail(attrs?.email || "");
        setSub(subValue);
        setNewsletter(newsletterValue);
        setNraId(nraValue);
        setUspsaId(uspsaValue);
        setUserRole(roleValue);
        setPerson(personValue);

        setDraftUsername(preferred);
        setDraftNewsletter(newsletterValue);
        setDraftNraId(nraValue);
        setDraftUspsaId(uspsaValue);

        if (subValue) {
          const qrPayload = buildProfileQrPayload({
            sub: subValue,
            name: preferred,
            email: attrs?.email || ""
          });
          const qr = await QRCode.toDataURL(qrPayload, { margin: 1, width: 320 });
          if (active) {
            setQrDataUrl(qr);
          }
        }
      } catch (_loadError) {
        if (!active) {
          return;
        }
        if (competitionAccount && isCompetitionTestAuthEnabled()) {
          try {
            const personValue = await getMyCompetitionPerson();
            if (!active || !personValue) {
              return;
            }

            const preferred = personValue.displayName || personValue.email || "Competition tester";
            const subValue = personValue.cognitoSub || personValue.personId || "";
            const nraValue = findMembershipId(personValue?.memberships, "nra", "nra-competitor");
            const uspsaValue = findMembershipId(personValue?.memberships, "uspsa");

            setUsername(preferred);
            setEmail(personValue.email || "");
            setSub(subValue);
            setNewsletter(false);
            setNraId(nraValue);
            setUspsaId(uspsaValue);
            setUserRole("student");
            setPerson(personValue);

            setDraftUsername(preferred);
            setDraftNewsletter(false);
            setDraftNraId(nraValue);
            setDraftUspsaId(uspsaValue);

            if (subValue) {
              const qrPayload = buildProfileQrPayload({
                sub: subValue,
                name: preferred,
                email: personValue.email || ""
              });
              const qr = await QRCode.toDataURL(qrPayload, { margin: 1, width: 320 });
              if (active) {
                setQrDataUrl(qr);
              }
            }
            return;
          } catch {
            // Fall through to the competition sign-in gate.
          }
        }
        if (competitionAccount) {
          setProfileSignInRequired(true);
          return;
        }
        navigate(profileSignInPath, { replace: true });
        return;
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadProfile();
    return () => {
      active = false;
    };
  }, [competitionAccount, navigate, profileSignInPath]);

  React.useEffect(() => {
    let active = true;

    async function loadDirectorAccess() {
      if (!competitionAccount || loading) {
        return;
      }

      try {
        const nextSignal = await loadCompetitionDirectorSignal();
        if (active) {
          setDirectorSignal(nextSignal);
        }
      } catch (_directorError) {
        if (active) {
          setDirectorSignal("unknown");
        }
      }
    }

    loadDirectorAccess();
    return () => {
      active = false;
    };
  }, [competitionAccount, loading]);

  const directorNote = useMemo(() => {
    if (!competitionAccount) {
      return null;
    }
    if (directorSignal === "checking") {
      return {
        eyebrow: "Director",
        label: "Checking access...",
        path: "/matches/manage",
      };
    }
    if (directorSignal === "unknown") {
      return {
        eyebrow: "Director",
        label: directorModeLabel(directorSignal),
        path: directorModePath(directorSignal),
      };
    }
    if (directorSignal === "tournament" || directorSignal === "organizer") {
      return {
        eyebrow: "Director",
        label: "Tournament Admin",
        path: directorModePath(directorSignal),
      };
    }
    if (directorSignal === "match") {
      return {
        eyebrow: "Director",
        label: directorModeLabel(directorSignal),
        path: directorModePath(directorSignal),
      };
    }
    return {
      eyebrow: "Director",
      label: directorModeLabel(directorSignal),
      path: directorModePath(directorSignal),
    };
  }, [competitionAccount, directorSignal]);

  const competitionStatusItems = useMemo(() => {
    if (!competitionAccount) {
      return [];
    }
    return [
      { label: "NRA ID", value: nraId ? "Set" : "Missing", state: nraId ? "is-set" : "is-missing" },
      { label: "USPSA ID", value: uspsaId ? "Set" : "Missing", state: uspsaId ? "is-set" : "is-missing" },
      { label: "Account", value: sub ? "Signed in" : "Missing", state: sub ? "is-set" : "is-missing" },
    ];
  }, [competitionAccount, nraId, sub, uspsaId]);

  function compactId(id) {
    const value = String(id || "");
    if (value.length <= 22) {
      return value;
    }
    return `${value.slice(0, 10)}...${value.slice(-8)}`;
  }

  async function copyUserId() {
    if (!sub) {
      return;
    }

    try {
      await navigator.clipboard.writeText(sub);
      setMessage("User ID copied.");
      setError("");
    } catch (_copyError) {
      setError("Unable to copy User ID");
    }
  }

  function enterEditMode() {
    setDraftUsername(username);
    setDraftNraId(nraId);
    setDraftUspsaId(uspsaId);
    setDraftNewsletter(newsletter);
    setMode("edit");
    setMessage("");
    setError("");
  }

  function cancelEditMode() {
    setMode("view");
    setMessage("");
    setError("");
  }

  async function saveProfileEdits() {
    setBusy(true);
    setError("");
    setMessage("");

    try {
      const usePersonOnlyTestAuth = competitionAccount && isCompetitionTestAuthEnabled();
      const cognitoUpdates = usePersonOnlyTestAuth
        ? []
        : [
          updateUserAttribute({
            userAttribute: {
              attributeKey: "preferred_username",
              value: draftUsername.trim()
            }
          }),
          updateUserAttribute({
            userAttribute: {
              attributeKey: "custom:subToNewsLetter",
              value: draftNewsletter ? "true" : "false"
            }
          })
        ];

      const personUpdate = competitionAccount
        ? updateMyCompetitionPerson({
          displayName: draftUsername.trim(),
          email,
          memberships: mergeCompetitionMemberships(person?.memberships, {
            nraId: draftNraId,
            uspsaId: draftUspsaId
          })
        })
        : Promise.resolve(null);

      const [, nextPerson] = await Promise.all([
        cognitoUpdates.length > 0 ? Promise.all(cognitoUpdates) : Promise.resolve([]),
        personUpdate
      ]);

      setUsername(draftUsername.trim());
      setNraId(draftNraId.trim());
      setUspsaId(draftUspsaId.trim());
      setNewsletter(draftNewsletter);
      if (nextPerson) {
        setPerson(nextPerson);
      }
      setMode("view");
      setMessage("Profile updated.");
    } catch (saveError) {
      setError(saveError?.message || "Unable to update profile");
    } finally {
      setBusy(false);
    }
  }

  async function handleSignOut() {
    setBusy(true);
    setError("");
    try {
      await signOut();
      navigate(profileSignInPath, { replace: true });
    } catch (signOutError) {
      setError(signOutError?.message || "Sign out failed");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <section className="auth-page">
        <Helmet>
          <title>{profileTitle}</title>
        </Helmet>
        <div className={`auth-card ${competitionAccount ? "auth-card--competition profile-loading-panel" : ""}`}>
          <div className="auth-context-label">{competitionAccount ? "Range Pocket Competition" : "ArmorySync"}</div>
          <h1>{profileHeading}</h1>
          <p>{competitionAccount ? "Checking competition account" : "Checking session..."}</p>
        </div>
      </section>
    );
  }

  if (competitionAccount && profileSignInRequired) {
    return (
      <section className="leaderboard-page competition-account-gate-page">
        <Helmet>
          <title>{profileTitle}</title>
        </Helmet>
        <div className="match-board-hero">
          <div>
            <h1>Competition Account</h1>
          </div>
        </div>
        <CompetitionAuthGate
          eyebrow="Competition account"
          title="Sign in to manage match identity"
          signInPath={profileSignInPath}
          links={[
            { to: "/matches", label: "Find Matches" },
            { to: "/matches/my", label: "My Matches" },
            { to: "/tournaments", label: "Tournaments" },
          ]}
          className="competition-account-gate-panel"
        >
          Your Range Pocket account stores competitor IDs and the shared sign-in identity used for match registration, check-in, and results history.
        </CompetitionAuthGate>
      </section>
    );
  }

  return (
    <section className="auth-page auth-page--profile">
      <Helmet>
        <title>{profileTitle}</title>
      </Helmet>

      <div className={`auth-card profile-shell ${competitionAccount ? "profile-shell--competition" : ""}`}>
        <div className="profile-topbar">
          <div>
            <h1>{profileHeading}</h1>
            <p className="auth-subtitle profile-subtitle">{profileSubtitle}</p>
          </div>
          <div
            className="profile-mode-switch"
            role="tablist"
            aria-label={competitionAccount ? "Competition account mode" : "Profile mode"}
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === "view"}
              className={`profile-mode-btn ${mode === "view" ? "active" : ""}`}
              onClick={() => setMode("view")}
              disabled={busy}
            >
              View
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "edit"}
              className={`profile-mode-btn ${mode === "edit" ? "active" : ""}`}
              onClick={enterEditMode}
              disabled={busy}
            >
              Edit
            </button>
          </div>
        </div>

        {mode === "view" && (
          <>
            {!competitionAccount && (
              <section className="profile-hero">
                <div className="profile-avatar" aria-hidden="true">
                  {(username || "U").slice(0, 1).toUpperCase()}
                </div>
                <div className="profile-hero-copy">
                  <h2>{username || "User"}</h2>
                  <p>{email || "Signed in"}</p>
                  <div className="profile-badge-row">
                    <span className="profile-badge">{newsletter ? "Newsletter: On" : "Newsletter: Off"}</span>
                    <span className="profile-badge">Role: {userRole === "instructor" ? "Instructor" : "Student"}</span>
                  </div>
                </div>
              </section>
            )}

            {competitionAccount && (
              <section className="profile-status-strip" aria-label="Competition account status">
                {competitionStatusItems.map((item) => (
                  <div key={item.label} className={`profile-status-item ${item.state}`}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </section>
            )}

            <div className="profile-grid">
              {competitionAccount ? (
                <>
                  <section className="profile-panel profile-panel--priority">
                    <h2>Registration Identity</h2>
                    <div className="profile-kv">
                      <span>NRA ID</span>
                      <strong>{nraId || "Not set"}</strong>
                    </div>
                    <div className="profile-kv">
                      <span>USPSA ID</span>
                      <strong>{uspsaId || "Not set"}</strong>
                    </div>
                    <div className="profile-kv">
                      <span>User ID</span>
                      <strong className="profile-mono" title={sub || ""}>{compactId(sub) || "-"}</strong>
                    </div>
                    <div className="profile-panel-actions">
                      <button type="button" className="profile-action-primary" onClick={enterEditMode} disabled={busy}>Edit Identity</button>
                      <button type="button" className="profile-action-secondary" onClick={copyUserId} disabled={!sub || busy}>Copy User ID</button>
                    </div>
                  </section>

                  <section className="profile-panel">
                    <h2>Sign-in Identity</h2>
                    <div className="profile-kv">
                      <span>Display Name</span>
                      <strong>{username || "User"}</strong>
                    </div>
                    <div className="profile-kv">
                      <span>Email</span>
                      <strong>{email || "-"}</strong>
                    </div>
                    <div className="profile-kv">
                      <span>Account</span>
                      <strong>Shared ArmorySync account</strong>
                    </div>
                  </section>
                </>
              ) : (
                <>
                  <section className="profile-panel">
                    <h2>Identity</h2>
                    <div className="profile-kv">
                      <span>Display Name</span>
                      <strong>{username || "User"}</strong>
                    </div>
                    <div className="profile-kv">
                      <span>Email</span>
                      <strong>{email || "-"}</strong>
                    </div>
                    <div className="profile-kv">
                      <span>User ID</span>
                      <strong className="profile-mono" title={sub || ""}>{compactId(sub) || "-"}</strong>
                    </div>
                    <div className="profile-panel-actions">
                      <button type="button" onClick={copyUserId} disabled={!sub || busy}>Copy User ID</button>
                    </div>
                  </section>

                  <section className="profile-panel">
                    <h2>Competition</h2>
                    <div className="profile-kv">
                      <span>NRA ID</span>
                      <strong>{nraId || "-"}</strong>
                    </div>
                    <div className="profile-kv">
                      <span>USPSA ID</span>
                      <strong>{uspsaId || "-"}</strong>
                    </div>
                    <div className="profile-kv">
                      <span>Newsletter Updates</span>
                      <strong>{newsletter ? "On" : "Off"}</strong>
                    </div>
                    <div className="profile-kv">
                      <span>Role</span>
                      <strong>{userRole === "instructor" ? "Instructor" : "Student"}</strong>
                    </div>
                  </section>
                </>
              )}
            </div>

            <section className="profile-panel profile-panel--matches">
              <div className="profile-panel-row">
                <h2>{competitionAccount ? "Workspace" : "Matches"}</h2>
              </div>
              <div className="profile-action-grid">
                <Link to="/matches/my" className="profile-action-card">
                  <span>Registered matches</span>
                  <strong>My Matches</strong>
                </Link>
              </div>
            </section>

            {qrDataUrl && (
              <section className="profile-panel profile-panel--qr">
                <div className="profile-panel-row">
                  <h2>{competitionAccount ? "Check-in QR" : "Profile QR"}</h2>
                  <a href={qrDataUrl} download={`armorysync-profile-${sub || "user"}.png`} className="detail-link">
                    Download
                  </a>
                </div>
                <p className="profile-qr-caption">
                  {competitionAccount
                    ? "Range Pocket user ID"
                    : "Encodes your user ID, display name, and email for quick profile linking."}
                </p>
                <img src={qrDataUrl} alt="Profile QR code" className="profile-qr" />
              </section>
            )}

            {competitionAccount && directorNote && (
              <section className="profile-panel profile-panel--staff">
                <div className="profile-panel-row">
                  <h2>Staff Access</h2>
                </div>
                {directorSignal === "checking" ? (
                  <div className="profile-action-card profile-action-card--director profile-action-card--pending">
                    <span>Director mode</span>
                    <strong>{directorNote.label}</strong>
                  </div>
                ) : (
                  <Link to={directorNote.path} className="profile-action-card profile-action-card--director">
                    <span>Director mode</span>
                    <strong>{directorNote.label}</strong>
                  </Link>
                )}
              </section>
            )}

            <div className="auth-actions">
              {!competitionAccount && (
                <button type="button" onClick={enterEditMode} disabled={busy}>
                  Edit Profile
                </button>
              )}
              <button type="button" onClick={handleSignOut} disabled={busy}>
                {busy ? "Signing out..." : "Sign Out"}
              </button>
              {!competitionAccount && (
                userRole === "instructor" ? (
                  <Link to="/profile/instructor-slots">Manage Class Slots</Link>
                ) : (
                  <Link to="/profile/my-bookings">My Bookings</Link>
                )
              )}
            </div>
          </>
        )}

        {mode === "edit" && (
          <section className="profile-panel profile-panel--edit">
            <div className="profile-edit-header">
              <h2>{competitionAccount ? "Edit Registration Identity" : "Edit"}</h2>
            </div>
            <p className="profile-edit-caption">
              {competitionAccount
                ? "Name and IDs used for match registration"
                : "Changes are saved to your Cognito profile attributes."}
            </p>
            <form
              className="auth-form profile-edit-form"
              onSubmit={(event) => {
                event.preventDefault();
                saveProfileEdits();
              }}
            >
              <label>
                {competitionAccount ? "Match Display Name" : "Display Name"}
                <input
                  type="text"
                  value={draftUsername}
                  onChange={(event) => setDraftUsername(event.target.value)}
                  required
                />
              </label>

              <label>
                NRA ID
                <input
                  type="text"
                  value={draftNraId}
                  onChange={(event) => setDraftNraId(event.target.value)}
                />
              </label>

              <label>
                USPSA ID
                <input
                  type="text"
                  value={draftUspsaId}
                  onChange={(event) => setDraftUspsaId(event.target.value)}
                />
              </label>

              {!competitionAccount && (
                <div className="profile-kv">
                  <span>Newsletter</span>
                  <button
                    type="button"
                    className={`profile-toggle ${draftNewsletter ? "on" : "off"}`}
                    onClick={() => setDraftNewsletter((value) => !value)}
                  >
                    {draftNewsletter ? "On" : "Off"}
                  </button>
                </div>
              )}

              <div className="auth-actions">
                <button type="submit" disabled={busy || !draftUsername.trim()}>
                  {busy ? "Saving..." : "Save"}
                </button>
                <button type="button" onClick={cancelEditMode} disabled={busy}>
                  Cancel
                </button>
              </div>
            </form>
          </section>
        )}

        {message && <p className="auth-message">{message}</p>}
        {error && <p className="auth-error">{error}</p>}
      </div>
    </section>
  );
}
export const AuthSignIn_Page = {
  path: "/auth/sign-in",
  element: <AuthForm mode="sign-in" />
};

export const AuthSignUp_Page = {
  path: "/auth/sign-up",
  element: <AuthForm mode="sign-up" />
};

export const AuthForgotPassword_Page = {
  path: "/auth/forgot-password",
  element: <ForgotPasswordPage />
};

export const Profile_Page = {
  path: "/profile",
  element: <ProfilePage />
};
